"use client";

import { useCallback } from "react";
import { SCROLL_TRACK_CLASS, useHorizontalScroll } from "@/shared/hooks/useHorizontalScroll";
import { ScrollNavButton, ScrollNavFade } from "./ScrollNavButton";
import { isHeaderNav } from "./scrollable-container.types";
import type { ScrollableContainerProps } from "./scrollable-container.types";

/**
 * Primitivo único de scroll horizontal dos widgets.
 *
 * Gestos e fluidez vivem no `useHorizontalScroll` — mesma implementação da
 * barra de categorias e do `HorizontalScroller` do big2be-ui (vitrines de
 * produto): touch nativo (com inércia), Shift + roda na horizontal, e
 * animação só nas setas. Ver o doc do hook para o porquê de cada escolha.
 *
 * Full-bleed (`edge`): o recuo lateral vive no próprio track (padding-inline +
 * scroll-padding-inline) e um spacer garante o gutter direito ao rolar até o
 * fim — o conteúdo sangra até a borda, com o 1º item alinhado ao recuo.
 *
 * Setas (`showNav` + `navAlign`): `top-left`/`top-right` renderizam um header
 * `[título ── nav]` acima; `top`/`center`/`bottom` flutuam sobre o track. Setas
 * auto-ocultam/desabilitam por lado quando não há para onde rolar.
 */
export function ScrollableContainer({
    children,
    navAlign = "center",
    title,
    showNav = false,
    edge = "none",
    align = "center",
    justify = "start",
    navSize = "md",
    navEdgeless = false,
    navRounded = "full",
    navVariant = "button",
    gap = 24,
    scrollStep = 300,
    className,
    onScroll,
}: ScrollableContainerProps) {
    const {
        ref: trackRef,
        canLeft,
        canRight,
        update,
        scrollBy,
    } = useHorizontalScroll({
        step: scrollStep,
        measure: showNav,
    });

    const handleScroll = useCallback(() => {
        if (showNav) update();
        onScroll?.();
    }, [showNav, update, onScroll]);

    // Recuo lateral resolvido em CSS: "page" → token; "none" → 0; senão literal.
    const edgeValue = edge === "page" ? "var(--page-gutter)" : edge === "none" ? "0px" : edge;
    // Gap: `null`/omitido → responsivo por classe; número → px; string → literal.
    const responsiveGap = gap === null || gap === undefined;
    const gapValue = responsiveGap ? undefined : typeof gap === "number" ? `${gap}px` : gap;
    // `space-between` só faz sentido com o wrapper ocupando 100% do track; senão
    // `w-max` colapsa a sobra. Com justify-between usamos `w-full` + `justify-between`.
    const justifyBetween = justify === "between";

    const track = (
        <div
            ref={trackRef}
            className={`${SCROLL_TRACK_CLASS} ${className ?? ""}`}
            style={{ paddingInlineStart: edgeValue, scrollPaddingInline: edgeValue }}
            onScroll={handleScroll}
        >
            {/* Wrapper interno: `w-max` (largura do conteúdo) + `mx-auto` centraliza
                quando os itens cabem; quando estouram, `w-max` excede o track e o
                overflow rola normalmente (sangrando até a borda com edge).
                O gutter direito vai no `padding-inline-end` DESTE wrapper (não do
                track): aqui não há overflow, então o padding-end é respeitado no
                fim do scroll — e, por ser padding, não recebe o column-gap do flex. */}
            <div
                className={[
                    "flex items-start",
                    // `space-between` precisa de largura total; senão o track cola os itens.
                    // `min-w-full` fica restrito ao alinhamento inicial: dá base
                    // para filhos em `%` (mosaic), enquanto `align="center"`
                    // conserva `w-max` para o `mx-auto` centralizar o conteúdo.
                    justifyBetween
                        ? "w-full justify-between"
                        : align === "start"
                          ? "w-max min-w-full"
                          : "w-max",
                    align === "start" || justifyBetween ? "mr-auto" : "mx-auto",
                    // Gap responsivo default quando `gap` é omitido/null.
                    responsiveGap ? "gap-2 md:gap-4 lg:gap-8" : "",
                ]
                    .filter(Boolean)
                    .join(" ")}
                style={{ columnGap: gapValue, paddingInlineEnd: edgeValue }}
            >
                {children}
            </div>
        </div>
    );

    if (!showNav) return track;

    // Estilo A — header próprio [título ── nav] (top-left / top-right).
    const titleNode = (
        <h2 className="min-w-0 flex-1 truncate text-lg font-semibold text-base-content md:text-xl">
            {title}
        </h2>
    );

    if (isHeaderNav(navAlign)) {
        const navFirst = navAlign === "top-left";
        const navGroup = (
            <div className="flex shrink-0 items-center gap-2">
                <ScrollNavButton
                    size={navSize ?? "sm"}
                    side="left"
                    disabled={!canLeft}
                    onClick={() => scrollBy("left")}
                />
                <ScrollNavButton
                    size={navSize ?? "sm"}
                    side="right"
                    disabled={!canRight}
                    onClick={() => scrollBy("right")}
                />
            </div>
        );

        return (
            <div>
                <div className="mb-3 flex items-center gap-4" style={{ paddingInline: edgeValue }}>
                    {navFirst ? (
                        <>
                            {navGroup}
                            {title ? titleNode : <span className="flex-1" />}
                        </>
                    ) : (
                        <>
                            {title ? titleNode : <span className="flex-1" />}
                            {navGroup}
                        </>
                    )}
                </div>
                {track}
            </div>
        );
    }

    // Estilo B — setas flutuando sobre o track (top / center / bottom).
    const floatY = navAlign; // "top" | "center" | "bottom"
    return (
        <div>
            {title ? (
                <div className="mb-3" style={{ paddingInline: edgeValue }}>
                    {titleNode}
                </div>
            ) : null}
            <div className="relative">
                {track}
                {canLeft ? (
                    navVariant === "edge-fade" ? (
                        <ScrollNavFade
                            size={navSize}
                            side="left"
                            onClick={() => scrollBy("left")}
                        />
                    ) : (
                        <ScrollNavButton
                            size={navSize}
                            side="left"
                            floating
                            floatY={floatY}
                            edgeless={navEdgeless}
                            rounded={navRounded}
                            onClick={() => scrollBy("left")}
                        />
                    )
                ) : null}
                {canRight ? (
                    navVariant === "edge-fade" ? (
                        <ScrollNavFade
                            size={navSize}
                            side="right"
                            onClick={() => scrollBy("right")}
                        />
                    ) : (
                        <ScrollNavButton
                            size={navSize}
                            side="right"
                            floating
                            floatY={floatY}
                            edgeless={navEdgeless}
                            rounded={navRounded}
                            onClick={() => scrollBy("right")}
                        />
                    )
                ) : null}
            </div>
        </div>
    );
}
